import { lazy, Suspense, type ReactNode } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import ProtectedRoute from '@/auth/ProtectedRoute';
import PermissionRoute from '@/auth/PermissionRoute';
import { ERP_ONLY, PLATFORM_BASE, PLATFORM_LOGIN_PATH, PLATFORM_ONLY, SHOW_ERP, SHOW_PLATFORM, platformPath } from '@/config/appTarget';

/**
 * madmin.tripza.in serves the console at the root. An old /platform/... address — a bookmark, a link in
 * an email — lands on the same screen without the prefix instead of a 404.
 */
function DropPlatformPrefix() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/platform(?=\/|$)/, '') || '/'}${search}${hash}`} replace />;
}

/**
 * Sends an old address on to `to`, keeping its query string and navigation state — the state says who
 * was sent to sign in and why (session expired, idle, no platform access).
 */
function ForwardTo({ to }: { to: string }) {
  const { search, hash, state } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace state={state} />;
}

function Lazy({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>}>
      {children}
    </Suspense>
  );
}

/** Import page for one vendor list (or the TDS tax table): /vendor-lists/:kind/import. */
function VendorListImportRoute() {
  const kind = useParams().kind ?? '';
  return <PoiImportPage key={kind} kind={`list:${kind}` as 'list:Tds'} />;
}

// Public & auth
import LoginPage from '@/pages/auth/LoginPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage';
import VerifyEmailPage from '@/pages/auth/VerifyEmailPage';
const LandingPage = lazy(() => import('@/pages/landing/LandingPage'));

/** Opened on a company's own address (its website, ERP or a portal): there is no TravelERP marketing page there. */
const ON_COMPANY_DOMAIN = typeof window !== 'undefined' && !isTravelErpHost(window.location.hostname);
const OnboardingPage = lazy(() => import('@/pages/onboarding/OnboardingPage'));
const TermsPage = lazy(() => import('@/pages/legal/LegalPages').then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('@/pages/legal/LegalPages').then((m) => ({ default: m.PrivacyPage })));

// Platform console (control plane, SuperAdmin)
import PlatformLoginPage from '@/pages/platform/PlatformLoginPage';
import PlatformLayout from '@/pages/platform/PlatformLayout';
// Eager: the console's top bar already imports it for the Settings highlight.
import PlatformSettingsPage from '@/pages/platform/PlatformSettingsPage';
import CompaniesListPage from '@/pages/company/CompaniesListPage';
const PlatformDashboardPage = lazy(() => import('@/pages/platform/PlatformDashboardPage'));
const AiOpsCenterPage = lazy(() => import('@/pages/platform/AiOpsCenterPage'));
const ProviderCatalogPage = lazy(() => import('@/pages/platform/ai/ProviderCatalogPage'));
const PlatformAiConfigurationPage = lazy(() => import('@/pages/platform/ai/PlatformAiConfigurationPage'));
const ProviderModelsPage = lazy(() => import('@/pages/platform/ai/ProviderModelsPage'));
const GstIntegrationPage = lazy(() => import('@/pages/platform/GstIntegrationPage'));
const OnboardingOptionsPage = lazy(() => import('@/pages/platform/OnboardingOptionsPage'));
const SubscriptionsPage = lazy(() => import('@/pages/platform/SubscriptionsPage'));
const TenantDetailPage = lazy(() => import('@/pages/platform/TenantDetailPage'));
const KycPage = lazy(() => import('@/pages/settings/KycPage'));
const SupportLoginPage = lazy(() => import('@/pages/auth/SupportLoginPage'));
const KycReviewPage = lazy(() => import('@/pages/platform/KycReviewPage'));
const KycQueuePage = lazy(() => import('@/pages/platform/KycReviewPage').then((m) => ({ default: m.KycQueuePage })));
const PendingOnboardingPage = lazy(() => import('@/pages/platform/PendingOnboardingPage'));
const GeographyMasterPage = lazy(() => import('@/pages/platform/GeographyMasterPage'));
const BanksMasterPage = lazy(() => import('@/pages/platform/BanksMasterPage'));
const HotelGstSlabsPage = lazy(() => import('@/pages/platform/HotelGstSlabsPage'));
const LibraryPage = lazy(() => import('@/pages/platform/LibraryPage'));
const DestinationsPage = lazy(() => import('@/pages/destinations/DestinationsPage'));
const DestinationFormPage = lazy(() => import('@/pages/destinations/DestinationFormPage'));
const DestinationDetailPage = lazy(() => import('@/pages/destinations/DestinationDetailPage'));
const DestinationImportPage = lazy(() => import('@/pages/destinations/DestinationImportPage'));
const PoisPage = lazy(() => import('@/pages/places/PoisPage'));
const PoiFormPage = lazy(() => import('@/pages/places/PoiFormPage'));
const PoiImportPage = lazy(() => import('@/pages/places/PoiImportPage'));
const VendorsPage = lazy(() => import('@/pages/vendors/VendorsPage'));
const VendorFormPage = lazy(() => import('@/pages/vendors/VendorFormPage'));
const VendorDetailPage = lazy(() => import('@/pages/vendors/VendorDetailPage'));
const HotelsPage = lazy(() => import('@/pages/hotels/HotelsPage'));
const HotelFormPage = lazy(() => import('@/pages/hotels/HotelFormPage'));
const HotelContractPage = lazy(() => import('@/pages/hotels/HotelContractPage'));
const HotelCalculatorPage = lazy(() => import('@/pages/hotels/HotelCalculatorPage'));
const HotelRateSheetPage = lazy(() => import('@/pages/hotels/HotelRateSheetPage'));
const HotelReviewPage = lazy(() => import('@/pages/hotels/HotelReviewPage'));
const HotelListsHubPage = lazy(() => import('@/pages/vendors/VendorListsPage').then((m) => ({ default: m.HotelListsHubPage })));
const VendorListsHubPage = lazy(() => import('@/pages/vendors/VendorListsPage').then((m) => ({ default: m.VendorListsHubPage })));
const VendorListPage = lazy(() => import('@/pages/vendors/VendorListsPage').then((m) => ({ default: m.VendorListPage })));
const TdsSectionsPage = lazy(() => import('@/pages/vendors/TdsSectionsPage'));
const ApprovalsPage = lazy(() => import('@/pages/vendors/ApprovalsPage'));
const VendorReviewPage = lazy(() => import('@/pages/vendors/ApprovalsPage').then((m) => ({ default: m.VendorReviewPage })));
const AgreementTemplatesPage = lazy(() => import('@/pages/vendors/AgreementTemplatesPage'));
const VendorRegistrationPage = lazy(() => import('@/pages/vendor-portal/VendorRegistrationPage'));
const PortalLoginPage = lazy(() => import('@/pages/vendor-portal/PortalLoginPage'));
const PortalForgotPasswordPage = lazy(() => import('@/pages/vendor-portal/PortalForgotPasswordPage'));
const PortalHomePage = lazy(() => import('@/pages/vendor-portal/PortalHomePage'));
const SubToursPage = lazy(() => import('@/pages/places/SubToursPage'));
const SubTourFormPage = lazy(() => import('@/pages/places/SubTourFormPage'));
const RoutesPage = lazy(() => import('@/pages/places/RoutesPage'));
const RouteFormPage = lazy(() => import('@/pages/places/RouteFormPage'));
const TourCategoriesPage = lazy(() => import('@/pages/masters/TourCategoriesPage'));

// Tenant ERP
import DashboardPage from '@/pages/dashboard/DashboardPage';
import SettingsLayout from '@/pages/settings/SettingsLayout';
import { BrandingSection, EmailSection, WhatsAppSection } from '@/pages/settings/sections/IntegrationSections';
import { BankAccountsSection, DocumentTemplatesSection, InvoiceSeriesSection } from '@/pages/settings/sections/CompanySetupSections';
import { SupportAccessSection } from '@/pages/settings/sections/SupportAccessSection';
import { PlacesDefaultsSection } from '@/pages/settings/sections/PlacesDefaultsSection';
import { OperatingRegionsSection } from '@/pages/settings/sections/OperatingRegionsSection';
import NotFound from '@/pages/NotFound';
import { isTravelErpHost } from '@/hooks/useLoginPage';
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
// Company setup, staff, branches and access are tabs of Business Profile, which pulls them in itself.
const BusinessProfilePage = lazy(() => import('@/pages/company/BusinessProfilePage'));
const UserDetailsPage = lazy(() => import('@/pages/admin/UserDetailsPage'));

// AI settings: only AI Setup is shown to a company; the old AI addresses open it.
const AiConfigurationPage = lazy(() => import('@/pages/settings/AiConfigurationPage'));

export default function App() {
  return (
    <Routes>
      {/* Platform Console (control plane - separate platform auth). At the root of madmin.tripza.in,
          under /platform in the combined app, and absent from the ERP build (see config/appTarget). */}
      {SHOW_PLATFORM && (
        <>
          <Route path={platformPath('/login')} element={PLATFORM_ONLY ? <ForwardTo to={PLATFORM_LOGIN_PATH} /> : <PlatformLoginPage />} />
          <Route path={PLATFORM_BASE || '/'} element={<PlatformLayout />}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Lazy><PlatformDashboardPage /></Lazy>} />
            <Route path="tenants" element={<CompaniesListPage />} />
            <Route path="tenants/:id" element={<Lazy><TenantDetailPage /></Lazy>} />
            <Route path="tenants/:id/kyc" element={<Lazy><KycReviewPage /></Lazy>} />
            <Route path="library" element={<Lazy><LibraryPage /></Lazy>} />
            <Route path="kyc" element={<Lazy><KycQueuePage /></Lazy>} />
            <Route path="onboarding/pending" element={<Lazy><PendingOnboardingPage /></Lazy>} />
            <Route path="subscriptions" element={<Lazy><SubscriptionsPage /></Lazy>} />
            <Route path="settings" element={<PlatformSettingsPage />} />
            <Route path="geography" element={<Lazy><GeographyMasterPage /></Lazy>} />
            <Route path="banks" element={<Lazy><BanksMasterPage /></Lazy>} />
            <Route path="hotel-gst" element={<Lazy><HotelGstSlabsPage /></Lazy>} />
            <Route path="ai/providers" element={<Lazy><ProviderCatalogPage /></Lazy>} />
            <Route path="ai/configuration" element={<Lazy><PlatformAiConfigurationPage /></Lazy>} />
            <Route path="ai/models" element={<Lazy><ProviderModelsPage /></Lazy>} />
            <Route path="ai-ops" element={<Lazy><AiOpsCenterPage /></Lazy>} />
            <Route path="integrations/gst" element={<Lazy><GstIntegrationPage /></Lazy>} />
            <Route path="onboarding-options" element={<Lazy><OnboardingOptionsPage /></Lazy>} />
          </Route>
          {PLATFORM_ONLY && <Route path="/platform/*" element={<DropPlatformPrefix />} />}
        </>
      )}

      {/* The tenant ERP — everything below is absent from the Platform build (see config/appTarget). */}
      {SHOW_ERP && (
        <>
          {/* On erp.tripza.in the address itself is the sign-in page; the marketing site lives on its own
              domain. The login page sends a signed-in user on to their dashboard. */}
          <Route path="/" element={ERP_ONLY || ON_COMPANY_DOMAIN ? <LoginPage /> : <Lazy><LandingPage /></Lazy>} />

          {/* Public. Every "back to sign in" link, the session guards and the API's emails still say /login. */}
          <Route path="/login" element={ERP_ONLY ? <ForwardTo to="/" /> : <LoginPage />} />
          {/* A company is created only through guided onboarding. The old address forwards there. */}
          <Route path="/register" element={<ForwardTo to="/onboarding" />} />
          <Route path="/onboarding" element={<Lazy><OnboardingPage /></Lazy>} />
          <Route path="/onboarding/success" element={<Navigate to="/login" replace />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/support-login" element={<Lazy><SupportLoginPage /></Lazy>} />
          {/* Linked from sign-up (O1). Public: read before an account exists. */}
          <Route path="/terms" element={<Lazy><TermsPage /></Lazy>} />
          <Route path="/privacy" element={<Lazy><PrivacyPage /></Lazy>} />
          {/* Module 2: vendor self-registration without a login (V10 open link, V12) and the vendor portal (V15–V18). */}
          <Route path="/join/vendor" element={<Lazy><VendorRegistrationPage /></Lazy>} />
          <Route path="/join/vendor/:token" element={<Lazy><VendorRegistrationPage /></Lazy>} />
          <Route path="/vendor-portal/login" element={<Lazy><PortalLoginPage /></Lazy>} />
          <Route path="/vendor-portal/forgot-password" element={<Lazy><PortalForgotPasswordPage /></Lazy>} />
          <Route path="/vendor-portal/*" element={<Lazy><PortalHomePage /></Lazy>} />

          {/* Authenticated */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />

              {/* Masters › Destinations (Module 0, M0.2) */}
              <Route element={<PermissionRoute anyOf={['City.View']} />}>
                <Route path="/destinations" element={<Lazy><DestinationsPage /></Lazy>} />
                <Route path="/destinations/:id" element={<Lazy><DestinationDetailPage /></Lazy>} />
              </Route>
              <Route element={<PermissionRoute anyOf={['City.Create']} />}>
                <Route path="/destinations/new" element={<Lazy><DestinationFormPage /></Lazy>} />
                <Route path="/destinations/import" element={<Lazy><DestinationImportPage /></Lazy>} />
              </Route>
              <Route element={<PermissionRoute anyOf={['City.Edit']} />}>
                <Route path="/destinations/:id/edit" element={<Lazy><DestinationFormPage /></Lazy>} />
              </Route>

              {/* Masters › Attractions (M1.2, A1–A5) and Activities (M1.3, C1); key resets state between the two lists */}
              {(['attractions', 'activities'] as const).map((k) => (
                <Route key={k}>
                  <Route element={<PermissionRoute anyOf={['City.View']} />}>
                    <Route path={`/${k}`} element={<Lazy><PoisPage key={k} kind={k} /></Lazy>} />
                    <Route path={`/${k}/:id`} element={<Lazy><PoiFormPage key={`${k}-view`} kind={k} /></Lazy>} />
                  </Route>
                  <Route element={<PermissionRoute anyOf={['City.Create']} />}>
                    <Route path={`/${k}/new`} element={<Lazy><PoiFormPage key={`${k}-new`} kind={k} /></Lazy>} />
                    <Route path={`/${k}/import`} element={<Lazy><PoiImportPage key={k} kind={k} /></Lazy>} />
                  </Route>
                  <Route element={<PermissionRoute anyOf={['City.Edit']} />}>
                    <Route path={`/${k}/:id/edit`} element={<Lazy><PoiFormPage key={`${k}-edit`} kind={k} /></Lazy>} />
                  </Route>
                </Route>
              ))}

              {/* Masters › Sub-tours (M1.4, S1–S4) and Travel times (M1.5, R1–R2) */}
              <Route element={<PermissionRoute anyOf={['City.View']} />}>
                <Route path="/sub-tours" element={<Lazy><SubToursPage /></Lazy>} />
                <Route path="/sub-tours/:id" element={<Lazy><SubTourFormPage key="view" /></Lazy>} />
                <Route path="/travel-times" element={<Lazy><RoutesPage /></Lazy>} />
                <Route path="/travel-times/:id" element={<Lazy><RouteFormPage key="view" /></Lazy>} />
              </Route>
              <Route element={<PermissionRoute anyOf={['City.Create']} />}>
                <Route path="/sub-tours/new" element={<Lazy><SubTourFormPage key="new" /></Lazy>} />
                <Route path="/sub-tours/import" element={<Lazy><PoiImportPage key="sub-tours" kind="sub-tours" /></Lazy>} />
                <Route path="/travel-times/new" element={<Lazy><RouteFormPage key="new" /></Lazy>} />
                <Route path="/travel-times/import" element={<Lazy><PoiImportPage key="travel-times" kind="travel-times" /></Lazy>} />
              </Route>
              <Route element={<PermissionRoute anyOf={['City.Edit']} />}>
                <Route path="/sub-tours/:id/edit" element={<Lazy><SubTourFormPage key="edit" /></Lazy>} />
                <Route path="/travel-times/:id/edit" element={<Lazy><RouteFormPage key="edit" /></Lazy>} />
              </Route>

              {/* Masters › Suppliers › Vendors (Module 2: V1–V5, V8, V9, V11) and the lists behind them (V19, V20) */}
              <Route element={<PermissionRoute anyOf={['Vendor.View']} />}>
                <Route path="/vendors" element={<Lazy><VendorsPage /></Lazy>} />
                <Route path="/vendors/:id" element={<Lazy><VendorDetailPage /></Lazy>} />
                <Route path="/vendor-lists" element={<Lazy><VendorListsHubPage /></Lazy>} />
                <Route path="/vendor-lists/tds" element={<Lazy><TdsSectionsPage /></Lazy>} />
                {/* Settings › Document templates › Vendor agreements (M2.9) */}
                <Route path="/settings/vendor-agreements" element={<Lazy><AgreementTemplatesPage /></Lazy>} />
              </Route>
              <Route element={<PermissionRoute anyOf={['Vendor.Create']} />}>
                <Route path="/vendors/new" element={<Lazy><VendorFormPage key="new" /></Lazy>} />
                <Route path="/vendors/import" element={<Lazy><PoiImportPage key="vendors" kind="vendors" /></Lazy>} />
              </Route>
              <Route element={<PermissionRoute anyOf={['Vendor.Edit']} />}>
                <Route path="/vendors/:id/edit" element={<Lazy><VendorFormPage key="edit" /></Lazy>} />
              </Route>
              {/* One list page for the vendor and the hotel lists (the hotel lists open from Hotel lists too) */}
              <Route element={<PermissionRoute anyOf={['Vendor.View', 'Hotel.View']} />}>
                <Route path="/vendor-lists/:kind" element={<Lazy><VendorListPage /></Lazy>} />
              </Route>
              {/* Masters › Suppliers › Hotels (Module 3: H1–H5, H14) and the lists behind them */}
              <Route element={<PermissionRoute anyOf={['Hotel.View']} />}>
                <Route path="/hotels" element={<Lazy><HotelsPage /></Lazy>} />
                <Route path="/hotel-lists" element={<Lazy><HotelListsHubPage /></Lazy>} />
                <Route path="/hotels/calculator" element={<Lazy><HotelCalculatorPage /></Lazy>} />
                <Route path="/hotels/:id/contracts/:cid" element={<Lazy><HotelContractPage key="view" view /></Lazy>} />
                <Route path="/hotels/:id/contracts/:cid/ai" element={<Lazy><HotelRateSheetPage /></Lazy>} />
              </Route>
              <Route element={<PermissionRoute anyOf={['Hotel.Create']} />}>
                <Route path="/hotels/new" element={<Lazy><HotelFormPage key="new" /></Lazy>} />
                <Route path="/hotels/import" element={<Lazy><PoiImportPage key="hotels" kind="hotels" /></Lazy>} />
              </Route>
              <Route element={<PermissionRoute anyOf={['Hotel.View']} />}>
                <Route path="/hotels/:id" element={<Lazy><HotelFormPage key="view" view /></Lazy>} />
              </Route>
              <Route element={<PermissionRoute anyOf={['Hotel.Edit']} />}>
                <Route path="/hotels/:id/edit" element={<Lazy><HotelFormPage key="edit" /></Lazy>} />
              </Route>
              {/* Hotel contracts (H7–H11) */}
              <Route element={<PermissionRoute anyOf={['Hotel.Contract']} />}>
                <Route path="/hotels/:id/contracts/new" element={<Lazy><HotelContractPage key="new" /></Lazy>} />
                <Route path="/hotels/:id/contracts/:cid/edit" element={<Lazy><HotelContractPage key="edit" /></Lazy>} />
              </Route>
              {/* Home › Approvals centre (M2.8: V13, V14, V21) */}
              <Route element={<PermissionRoute anyOf={['Vendor.Approve']} />}>
                <Route path="/approvals/vendors/:id/review" element={<Lazy><VendorReviewPage /></Lazy>} />
              </Route>
              <Route element={<PermissionRoute anyOf={['Vendor.Approve', 'Hotel.Approve']} />}>
                <Route path="/approvals" element={<Lazy><ApprovalsPage /></Lazy>} />
              </Route>
              <Route element={<PermissionRoute anyOf={['Hotel.Approve']} />}>
                <Route path="/approvals/hotels/:kind/:id" element={<Lazy><HotelReviewPage /></Lazy>} />
              </Route>
              <Route element={<PermissionRoute anyOf={['VendorList.Manage']} />}>
                <Route path="/vendor-lists/:kind/import" element={<Lazy><VendorListImportRoute /></Lazy>} />
              </Route>

              {/* Masters › Tour categories (FR-TOUR-09; the O4 themes become categories) */}
              <Route element={<PermissionRoute anyOf={['Company.View']} />}>
                <Route path="/tour-categories" element={<Lazy><TourCategoriesPage /></Lazy>} />
              </Route>

              {/* Files */}
              <Route element={<PermissionRoute anyOf={['Company.View']} />}>
                {/* File Library is hidden: images are managed inside each master. Old links go to the dashboard. */}
                <Route path="/files" element={<Navigate to="/dashboard" replace />} />
              </Route>

              {/* Administration — company setup, staff, branches and access are tabs of Business Profile. */}
              <Route element={<PermissionRoute anyOf={['Company.View', 'User.View', 'Branch.View', 'Role.View', 'Permission.View']} />}>
                <Route path="/business-profile" element={<Lazy><BusinessProfilePage /></Lazy>} />
                <Route path="/business-profile/:tab" element={<Lazy><BusinessProfilePage /></Lazy>} />
                <Route path="/business-profile/:tab/:section" element={<Lazy><BusinessProfilePage /></Lazy>} />
              </Route>
              <Route path="/company" element={<Navigate to="/business-profile/company" replace />} />
              <Route path="/admin/users" element={<Navigate to="/business-profile/staff" replace />} />
              <Route path="/admin/branches" element={<Navigate to="/business-profile/branches" replace />} />
              <Route path="/admin/roles" element={<Navigate to="/business-profile/roles/roles" replace />} />
              <Route path="/admin/access" element={<Navigate to="/business-profile/roles/assignment" replace />} />
              <Route path="/admin/permissions" element={<Navigate to="/business-profile/roles/permissions" replace />} />
              <Route element={<PermissionRoute anyOf={['User.View']} />}>
                <Route path="/admin/users/:id" element={<Lazy><UserDetailsPage /></Lazy>} />
              </Route>

              {/* Settings Center */}
              <Route element={<PermissionRoute anyOf={['Company.View']} />}>
                <Route path="/settings/ai/configuration" element={<Lazy><AiConfigurationPage /></Lazy>} />
                <Route path="/settings/ai/*" element={<Navigate to="/settings/ai/configuration" replace />} />
                <Route path="/settings/ai" element={<Navigate to="/settings/ai/configuration" replace />} />
                <Route path="/settings/kyc" element={<Lazy><KycPage /></Lazy>} />
                <Route path="/settings" element={<SettingsLayout />}>
                  <Route index element={<Navigate to="branding" replace />} />
                  <Route path="company" element={<Navigate to="/business-profile/company" replace />} />
                  <Route path="branding" element={<BrandingSection />} />
                  <Route path="email" element={<EmailSection />} />
                  <Route path="whatsapp" element={<WhatsAppSection />} />
                  <Route path="support" element={<SupportAccessSection />} />
                  <Route path="bank-accounts" element={<BankAccountsSection />} />
                  <Route path="invoice-series" element={<InvoiceSeriesSection />} />
                  <Route path="documents" element={<DocumentTemplatesSection />} />
                  <Route path="regions" element={<OperatingRegionsSection />} />
                  <Route path="places" element={<PlacesDefaultsSection />} />
                </Route>
              </Route>

              <Route path="/profile" element={<Lazy><ProfilePage /></Lazy>} />
            </Route>
          </Route>
        </>
      )}

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
